import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import {
  InteractionChannelAuditRequest,
  ResponsesFixedIncomeOrderCreateResponse,
} from '@genial/services/dist/models';
import React from 'react';

import { useSendingChannels } from '../../../../../../../../hooks/useSendingChannels';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useFixedIncomeAssetController } from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/FixedIncome';
import {
  orderResumePdfTemplate,
  parsePDFDataExport,
  setupPDF,
} from '../../../../../../../Resume/utils';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { SimplifiedApplicationMissingTerm } from '../../components/SimplifiedApplicationMissingTerm';
import { TicketApplicationInProgress } from '../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../components/TicketApplicationSuccessDetails';
import { defaultTicketApplicationBaseInfo } from '../../components/TicketApplicationSuccessDetails/constants';
import { TicketDetails } from '../../components/TicketDetails';
import {
  defaultErrorMessageApplication,
  screenName,
  screenNameSimplifiedFlow,
} from '../../constants';
import { CustomerApplicationModalProductProps } from '../../types';

import { FixedIncomeInvestCard } from './components/FixedIncomeInvestCard';
import { getFixedIncomeSuccessInfos, updateSuccessInfosToExport } from './constants';

export interface FixedIncomeApplicationProps
  extends CustomerApplicationModalProductProps<'fixedIncome'> {
  onClose: () => void;
  handleFollowTicket: () => void;
}

export const FixedIncomeApplication: React.FC<FixedIncomeApplicationProps> = ({
  offer,
  onClose,
  customerId,
  productType,
  accountNumber,
  handleFollowTicket,
  isSimplifiedApplication,
  isSimplifiedApplicationFlagMatched,
  showSendingChannels,
  clientHasTerm,
  isClientTermLoading,
}) => {
  const { showToastFromError, showToast } = useToastCreator('fixedIncomeApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [successInfos, setSuccessInfos] = React.useState(defaultTicketApplicationBaseInfo);
  const [interactionChannelAudit, setInteractionChannelAudit] =
    React.useState<InteractionChannelAuditRequest | null>();
  const [ticketSuccessData, setTicketSuccessData] =
    React.useState<ResponsesFixedIncomeOrderCreateResponse>();

  const offerController = useFixedIncomeAssetController(offer);
  const sendingChannelsController = useSendingChannels(customerId);

  const buttonDisabled =
    !!offerController.hasError ||
    (!sendingChannelsController.sendingChannelsSelected.length && !isSimplifiedApplication) ||
    interactionChannelAudit === undefined;

  const handleExportNewOrder = () => {
    if (!ticketSuccessData) {
      return;
    }
    const updatedSuccessInfos = updateSuccessInfosToExport(ticketSuccessData, offerController);
    const data = parsePDFDataExport(updatedSuccessInfos as any);
    setupPDF(orderResumePdfTemplate(data, 'FixedIncome'), `ordem-${successInfos.orderHash}.pdf`);
  };

  const handleConfirmOrder = () => {
    setSendingOrder(true);
    Service.FixedIncome.V1.postOrdersBuy({
      screenName: isSimplifiedApplication ? screenNameSimplifiedFlow : screenName,
      fixedIncomeOrderCreateRequest: {
        requestedOfferDetail: { ...offerController.selectedFee },
        accountNumber,
        orderAmount: offerController.currentValue as number,
        productId: offer.productIdNormalized,
        quantity: undefined as unknown as number,
        notificationChannels: isSimplifiedApplication
          ? null
          : sendingChannelsController.sendingChannelsSelected,
        orderName: 'Renda Fixa',
        interactionChannelAudit: interactionChannelAudit!,
      },
    })
      .then(({ data }) => {
        setSuccess(true);
        setTicketSuccessData(data);
        setSuccessInfos(
          getFixedIncomeSuccessInfos(
            data.offer,
            data.order,
            offerController,
            showSendingChannels,
            isSimplifiedApplicationFlagMatched
          )
        );
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessageApplication);
      })
      .finally(() => setSendingOrder(false));
  };

  if (success) {
    return (
      <TicketApplicationSuccessDetails
        {...successInfos}
        onClose={onClose}
        onFollow={handleFollowTicket}
        onExportPdf={handleExportNewOrder}
      />
    );
  }

  if (sendingOrder) {
    return <TicketApplicationInProgress />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <CardHeader onClose={onClose} />

      <FixedIncomeInvestCard offer={offer} offerController={offerController} />

      <SimplifiedApplicationMissingTerm
        isClientTermAvailable={clientHasTerm}
        isClientTermLoading={isClientTermLoading}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <TicketDetails
        productTypeId={productType}
        sendingChannelsController={sendingChannelsController}
        setInteractionChannelAudit={setInteractionChannelAudit}
        interactionChannelAudit={interactionChannelAudit}
        showSendingChannels={showSendingChannels}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <ConfirmationButton onConfirm={handleConfirmOrder} disabled={buttonDisabled} />
    </VStack>
  );
};
